import { useEffect, useState } from "react"
import { useField, useFormikContext } from "formik"
import _ from "lodash"

//dnd-kit components
import {
  rectIntersection,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { arrayMove } from "@dnd-kit/sortable"

//Contexts
import { AssessmentFormulaBuilderContext } from "../../../../../context/assessmentFormulaBuilderContext"

//Formula builder components
import FormulaBuilderFunctions from "./FormulaBuilderFunctions"
import FormulaBuilderOperators from "./FormulaBuilderOperators"
import FormulaBuilderNumbers from "./FormulaBuilderNumbers"
import FormulaBuilderQuestions from "./FormulaBuilderQuestions"
import FormulaBuilderLegend from "./FormulaBuilderLegend"
import FormulaBuilderForm from "./FormulaBuilderForm"
import FormulaBuilderDragOverlay from "./FormulaBuilderDragOverlay"
import formulaBuilderTypes from "./formulaBuilderTypes"
import FormulaBuilderPreview from "./FormulaBuilderPreview"
import FormulaBuilderMatrixQuestions from "./FormulaBuilderMatrixQuestions"
import elementTypes from "../Elements/elementTypes"

//Helpers
import { LogDebug } from "../../../../../helpers/logger"
import {
  dndDragOverConnector,
  dndDragOverFormulaBuilder,
  dndDragOverFunction,
  dndDragOverGroup,
  dndRemoveFromFunction,
  dndRemoveFromGroup,
} from "./Helpers/dndDragOverHelpers"
import { updateOrdering } from "../helpers/dndElementHelper"

//PrimeReact
import { Dialog } from "primereact/dialog"
import { Button } from "primereact/button"
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog"
import { classNames } from "primereact/utils"

//Styles
import myStyles from "../../../../../styles/assessments/ManagementFormulaBuilder.module.css"
import managementStyles from "../../../../../styles/assessments/ManagementForm.module.css"
import FormulaBuilderMultiAnswer from "./FormulaBuilderMultiAnswer"
import { findFormulaOrder } from "./Helpers/formulaHelpers"
import {
  applyNewFormulas,
  getMatrixRowLabelFormikName,
} from "./Helpers/matrixHelpers"

export default function FormulaBuilder({
  formikObjectName,
  parentFormikObjectName,
  questionOptions,
  onClose,
}) {
  const { values: formikValues, setFieldValue } = useFormikContext()

  //Matrix type question support:
  const [questionLabelIndex, setQuestionLabelIndex] = useState(0)

  //Formik field identifiers
  const [useFieldName, setUseFieldName] = useState(formikObjectName)
  const [parentField] = useField(parentFormikObjectName)
  const [formulaField, , formulaFieldHelpers] = useField(useFieldName)

  const isMatrix = elementTypes.isMatrixType(parentField.value.type)
  const isMultiAnswerType = elementTypes.isMultiAnswerType(
    parentField.value.type,
  )

  const [draggingObject, setDraggingObject] = useState(undefined)
  const [handledOverId, setHandledOverId] = useState(undefined)
  const [overConnectorId, setOverConnectorId] = useState(undefined)
  const [newFormulaOrder, setNewFormulaOrder] = useState(undefined) //A newly created formulaOrderObject added during current drag
  const [activeFunction, setActiveFunction] = useState(undefined)
  const [activeGroup, setActiveGroup] = useState(undefined)
  const [formulas, setFormulas] = useState(formulaField.value || [])
  const [tempFormulas, setTempFormulas] = useState(undefined)
  const [defaultOperator, setDefaultOperator] = useState(
    formulaBuilderTypes.operatorNames.addOperator,
  )
  const [deleteFormulaOrder, setDeleteFormulaOrder] = useState(undefined)

  const useFormulas = tempFormulas || formulas

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor),
  )

  useEffect(() => {
    if (isMatrix) {
      const newFieldName = getMatrixRowLabelFormikName(
        parentFormikObjectName,
        questionLabelIndex,
      )

      setUseFieldName(newFieldName)

      const formulas = _.get(formikValues, newFieldName, [])
      setFormulas(formulas)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentFormikObjectName, questionLabelIndex, isMatrix])

  useEffect(() => {
    if (deleteFormulaOrder?.formulaOrderid) {
      let newFormulas = _.cloneDeep(formulas)

      //find the formulaOrder to delete
      const formulaOrder = findFormulaOrder(
        deleteFormulaOrder.formulaOrderid,
        newFormulas,
      )

      if (formulaOrder) {
        formulaOrder.isDeleted = true

        if (formulaOrder.value) {
          formulaOrder.value.isDeleted = true
        } else if (formulaOrder.question) {
          formulaOrder.question.isDeleted = true
        } else if (formulaOrder.function) {
          formulaOrder.function.isDeleted = true
          formulaOrder.function.values.forEach(
            (value) => (value.isDeleted = true),
          )
        } else if (formulaOrder.group) {
          formulaOrder.group.isDeleted = true
          formulaOrder.group.formulas.forEach((value) => {
            value.isDeleted = true
            if (value.function) {
              value.function.values.forEach(
                (functionValue) => (functionValue.isDeleted = true),
              )
            }
          })
        }

        setFormulas(updateOrdering(newFormulas))
        setDeleteFormulaOrder(undefined)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteFormulaOrder])

  const DialogHeader = () => {
    return (
      <div className={myStyles.dialogHeaderContainer}>
        <div className={myStyles.dialogHeaderText}>Score calculation</div>
        <div className={myStyles.dialogHeaderInstruction}>
          <div className={myStyles.instructionContainer}>
            Drag and drop Operators, Numbers, Questions, and Functions onto the
            Builder to design simple and complex calculations.
          </div>
        </div>
        <div className={`${myStyles.dialogHeaderActions}`}>
          <Button
            className={`${managementStyles.button}`}
            onClick={() => {
              onClose()
            }}
          >
            <span className="material-icons">{"close"}</span>
          </Button>
        </div>
      </div>
    )
  }

  const handleDragMove = (event) => {
    const { active, over } = event

    if (active && over) {
      const useActiveFormulaOrder =
        newFormulaOrder || active?.data?.current?.formulaOrder

      //Check if we are dragging an operator over a connector
      if (
        active.data?.current?.type ===
          formulaBuilderTypes.draggableTypes.operator &&
        over?.id?.includes(formulaBuilderTypes.droppableIds.connector)
      ) {
        setOverConnectorId(over.id)
      } else if (overConnectorId) {
        setOverConnectorId(undefined)
      }

      //Handle dropping new items onto FormulaBuilderForm
      if (event?.over.id === "FormulaBuilderForm" && !useActiveFormulaOrder) {
        const results = dndDragOverFormulaBuilder(
          draggingObject,
          useFormulas,
          defaultOperator,
        )

        setTempFormulas(results?.newFormulas)
        setNewFormulaOrder(results?.newFormulaOrder)
      } else if (
        event?.over.id === "FormulaBuilderForm" &&
        !useActiveFormulaOrder?.group &&
        useActiveFormulaOrder.groupId
      ) {
        //This is a formula being dragged out of a group
        LogDebug("handleDragMove: dragging formula out of group")
        const results = dndRemoveFromGroup(useActiveFormulaOrder, useFormulas)

        setTempFormulas(results?.newFormulas)
        setActiveGroup(undefined)
      } else if (
        (event?.over.id === "FormulaBuilderForm" ||
          event?.over.id.includes("FormulaGroup_")) &&
        !useActiveFormulaOrder?.function &&
        useActiveFormulaOrder?.functionId
      ) {
        //This is a formula being dragged out of a group
        const results = dndRemoveFromFunction(
          useActiveFormulaOrder,
          useFormulas,
        )

        if (results) {
          setTempFormulas(results?.newFormulas)
          setActiveGroup(results?.activeGroup)
          setActiveFunction(undefined)
        }

        setHandledOverId(event?.over.id)
      } else if (
        event?.over.id.includes("connector_") &&
        event?.active?.data?.current?.type === "operator"
      ) {
        //This is an operator being dragged over a connector
        const results = dndDragOverConnector(
          event.active.data.current,
          event.over.id,
          useFormulas,
        )

        if (results) {
          setTempFormulas(results)
          setHandledOverId(event.over.id)
        }
      } else if (
        event?.active?.data?.current?.type === "operator" &&
        !event?.over?.id?.includes("connector_")
      ) {
        //This is an operator being dragged that is not over a connector
        setTempFormulas(undefined)
      } else if (
        !draggingObject?.formulaOrder?.group &&
        event?.active.id !== formulaBuilderTypes.operatorNames.groupOperator &&
        event?.over.id.includes("FormulaGroup_") &&
        !["operator"].includes(draggingObject.type) //ignored types for grouping
      ) {
        //Handle hovering over a group's droppable area'
        const results = dndDragOverGroup(
          draggingObject, //The object being dragged
          event.over.id, //The group ID that the object is dragged over
          useActiveFormulaOrder, //The current formulaOrder associated with the dragged object
          useFormulas,
          defaultOperator,
        )

        setTempFormulas(results?.newFormulas)
        setActiveGroup(results?.activeGroup)

        if (newFormulaOrder) {
          setNewFormulaOrder(results?.newFormulaOrder)
        }

        setHandledOverId(event?.over.id)
      } else if (
        !draggingObject?.formulaOrder?.function &&
        event?.active.id !== formulaBuilderTypes.operatorNames.groupOperator &&
        event?.over.id.includes("FormulaFunction_") &&
        !["operator"].includes(draggingObject.type) //ignored types for grouping
      ) {
        LogDebug("handleDragMove: dragging over function")
        //Handle a question or number being dragged over a function's droppable area
        const results = dndDragOverFunction(
          draggingObject, //The object being dragged
          event.over.id, //The group ID that the object is dragged over
          useActiveFormulaOrder, //The current formulaOrder associated with the dragged object
          useFormulas,
          defaultOperator,
        )

        if (results) {
          setTempFormulas(results?.newFormulas)
          setActiveGroup(results?.activeGroup)
          setActiveFunction(results?.activeFunction)

          if (newFormulaOrder) {
            setNewFormulaOrder(results?.newFormulaOrder)
          }
        }

        setHandledOverId(event?.over.id)
      } else if (
        //Handle reordering formulas
        useActiveFormulaOrder &&
        over &&
        over?.data?.current?.formulaOrder &&
        useActiveFormulaOrder.id !== over.id &&
        over.id !== handledOverId
      ) {
        const handleGroup = activeGroup
        const handleFunction = activeFunction

        let oldIndex = undefined
        let newIndex = undefined
        let formulaList = _.cloneDeep(useFormulas)
        let sourceList = formulaList

        LogDebug(`Handle group: ${!!handleGroup}`)
        LogDebug(`Handle Function: ${!!handleFunction}`)
        LogDebug(`Object being dragged:`, useActiveFormulaOrder)

        if (handleGroup) {
          LogDebug("handleDragOver: handle reordering group formulas")
          sourceList = formulaList.find(
            (formulaOrder) => formulaOrder.group?.id === activeGroup.group.id,
          )

          oldIndex = sourceList?.group?.formulas?.findIndex(
            (value) => value.id === useActiveFormulaOrder.id,
          )
          newIndex = sourceList?.group?.formulas?.findIndex(
            (value) => value.id === over.id,
          )
        } else if (handleFunction) {
          LogDebug("handleDragOver: handle reordering function formulas")
          sourceList = formulaList.find(
            (formulaOrder) =>
              formulaOrder.function?.id === activeFunction.function.id,
          )

          oldIndex = sourceList?.function?.values?.findIndex(
            (value) => value.id === useActiveFormulaOrder.id,
          )
          newIndex = sourceList?.function?.values?.findIndex(
            (value) => value.id === over.id,
          )

          LogDebug(
            `handleDragOver: handle reordering function formulas [${oldIndex} -> ${newIndex}]`,
          )
        } else {
          oldIndex = useFormulas.findIndex(
            (value) => value.id === useActiveFormulaOrder.id,
          )

          newIndex = useFormulas.findIndex((value) => value.id === over.id)
        }

        if (oldIndex >= 0 && newIndex >= 0 && sourceList !== undefined) {
          if (oldIndex === 0) {
            if (sourceList[oldIndex]?.function) {
              sourceList[oldIndex].function.connectingOperator = defaultOperator
            } else if (sourceList[oldIndex]?.group) {
              sourceList[oldIndex].group.connectingOperator = defaultOperator
            } else if (sourceList[oldIndex]?.value) {
              sourceList[oldIndex].value.connectingOperator = defaultOperator
            }
          }

          if (handleGroup) {
            LogDebug("handleDragOver: persist group changes")
            sourceList.group.formulas = updateOrdering(
              arrayMove(sourceList.group.formulas, oldIndex, newIndex),
            )

            setTempFormulas(formulaList)
          } else if (handleFunction) {
            LogDebug("handleDragOver: persist function changes")
            sourceList.function.values = updateOrdering(
              arrayMove(sourceList.function.values, oldIndex, newIndex),
            )

            setTempFormulas(formulaList)
          } else {
            setTempFormulas(arrayMove(sourceList, oldIndex, newIndex))
          }

          setHandledOverId(over.id)
        }
      }
    } else if (tempFormulas) {
      setTempFormulas(undefined)
      setHandledOverId(undefined)
      setNewFormulaOrder(undefined)
    } else if (handledOverId) {
      setHandledOverId(undefined)
    }
  }

  const clearDragContext = () => {
    setDraggingObject(undefined)
    setTempFormulas(undefined)
    setHandledOverId(undefined)
    setNewFormulaOrder(undefined)
    setActiveGroup(undefined)
    setActiveFunction(undefined)
  }

  const handleDragEnd = (event) => {
    if (tempFormulas) {
      setFormulas(tempFormulas)
    }

    clearDragContext()
  }

  const onUpdateFormulaEntry = (newEntry) => {
    let newFormulas = _.cloneDeep(formulas)
    let entryFound = false

    const updatedFormulas = newFormulas.map((formula) => {
      if (entryFound) {
        return formula
      } else if (formula.id === newEntry.id) {
        //If this is the entry being updated, return the new version
        return newEntry
      } else if (formula.group) {
        //If this is a group, see if the entry is in the group
        //If it is, replace it with the new version
        formula.group.formulas = formula.group.formulas.map((groupFormula) => {
          if (groupFormula.id === newEntry.id) {
            return newEntry
          } else {
            return groupFormula
          }
        })
      }

      //return the formula (updated or not)
      return formula
    })

    setFormulas(updatedFormulas)
  }

  const contextValue = {
    draggingObject,
    newFormulaOrder,
    formulas,
    onUpdateFormulaEntry,
    onDeleteFormulaOrder: (formulaOrderid, formulaGroupId) => {
      setDeleteFormulaOrder({ formulaOrderid, formulaGroupId })
    },
  }

  const removeNewDeletedEntries = (entries) => {
    return entries.filter((entry) => !(entry.isNew && entry.isDeleted))
  }

  const saveChanges = () => {
    const recursivelyClean = (items) => {
      if (!items) return []
      return removeNewDeletedEntries(items).map((item) => {
        const newItem = _.cloneDeep(item)
        if (newItem.group) {
          newItem.group.formulas = recursivelyClean(newItem.group.formulas)
        } else if (newItem.function) {
          newItem.function.values = recursivelyClean(newItem.function.values)
        }
        return newItem
      })
    }

    //Remove newly created entries that were deleted before saving
    const cleanedFormulas = recursivelyClean(formulas)

    //Fix the order numbers of the formulas
    const saveFormulas = cleanedFormulas.map((formula, index) => ({
      ...formula,
      orderNumber: index,
    }))

    formulaFieldHelpers.setValue(saveFormulas)
  }

  const applyFormulaToAllMatrixQuestions = () => {
    const options = parentField?.value?.questionLabels || []

    options.forEach((option, index) => {
      if (index !== questionLabelIndex) {
        const rowFieldName = getMatrixRowLabelFormikName(
          parentFormikObjectName,
          index,
        )

        const currentFormulas = _.cloneDeep(
          _.get(formikValues, rowFieldName, []),
        )

        const clonedFormulas = _.cloneDeep(formulas)
        const newFormulas = applyNewFormulas(currentFormulas, clonedFormulas)

        setFieldValue(rowFieldName, newFormulas)
      }
    })
  }

  return (
    <Dialog
      header={DialogHeader}
      visible={true}
      onHide={onClose}
      className={myStyles.dialog}
      closable={false}
      resizable={true}
    >
      <ConfirmDialog />
      <AssessmentFormulaBuilderContext.Provider value={contextValue}>
        <DndContext
          sensors={sensors}
          collisionDetection={rectIntersection}
          onDragStart={(e) => {
            if (e?.active?.data?.current) {
              LogDebug(`Dragging object:`, e.active.data.current)

              setDraggingObject(e.active.data.current)

              if (e.active.data.current?.formulaOrder) {
                if (e.active.data.current?.formulaOrder?.groupId) {
                  //The dragged item is in a group, find the group
                  const group = useFormulas.find(
                    (formulaOrder) =>
                      formulaOrder.group?.id ===
                      e.active.data.current?.formulaOrder?.groupId,
                  )

                  if (group) {
                    setActiveGroup(group)
                  } else {
                    setActiveGroup(undefined)
                  }
                } else if (e.active.data.current.formulaOrder.functionId) {
                  //The dragged item is in a function, find the function
                  const func = useFormulas.find(
                    (formulaOrder) =>
                      formulaOrder.function?.id ===
                      e.active.data.current?.formulaOrder?.functionId,
                  )

                  if (func) {
                    setActiveFunction(func)
                  } else {
                    setActiveFunction(undefined)
                  }
                }
              }
            } else {
              setDraggingObject(undefined)
            }

            if (e?.active?.data?.current?.type === "operator") {
              setDefaultOperator(e.active.id)
            }
          }}
          onDragMove={handleDragMove}
          onDragCancel={clearDragContext}
          onDragEnd={(e) => {
            handleDragEnd(e)
          }}
        >
          <FormulaBuilderDragOverlay object={draggingObject} />
          <div className={myStyles.mainLayout}>
            <div className={classNames(myStyles.mainLayoutFunctions)}>
              <div className={myStyles.mainLayoutFunctionsScroller}>
                <FormulaBuilderFunctions />
              </div>
            </div>
            <div className={myStyles.mainLayoutForm}>
              <FormulaBuilderForm
                isDragging={!!draggingObject}
                isMatrix={isMatrix}
                draggingType={draggingObject?.type}
                draggingNewFormulaOrder={newFormulaOrder}
                formulas={useFormulas || []}
                overConnectorId={overConnectorId}
                defaultOperator={defaultOperator}
                onApplyToAllMatrixQuestions={applyFormulaToAllMatrixQuestions}
              />
            </div>
            <div className={myStyles.mainLayoutPreview}>
              <FormulaBuilderPreview options={parentField?.value?.options} />
            </div>

            <div className={`${myStyles.mainLayoutPalette}`}>
              {isMatrix && (
                <div
                  className={classNames(
                    myStyles.container,
                    myStyles.groupingContainer,
                    myStyles.mainMatrixQuestionContainer,
                  )}
                >
                  <FormulaBuilderMatrixQuestions
                    parentFormikObjectName={parentFormikObjectName}
                    questionLabels={parentField?.value?.questionLabels || []}
                    selectedIndex={questionLabelIndex}
                    onSelectedIndexChanged={(value) => {
                      if (!_.isEqual(formulas, formulaField.value || [])) {
                        confirmDialog({
                          message: (
                            <>
                              There are unsaved changes that will be lost when
                              selecting the new question. <br />
                              Do you want to save changes now?
                            </>
                          ),
                          header: "Confirmation",
                          icon: "pi pi-exclamation-triangle",
                          defaultFocus: "accept",
                          closable: true,
                          accept: () => {
                            saveChanges()
                            setQuestionLabelIndex(value)
                          },
                          reject: () => {
                            setQuestionLabelIndex(value)
                          },
                        })
                      } else {
                        setQuestionLabelIndex(value)
                      }
                    }}
                  />
                </div>
              )}
              <div
                className={classNames(
                  myStyles.container,
                  myStyles.groupingContainer,
                )}
              >
                <FormulaBuilderOperators
                  activeOperator={defaultOperator}
                  onActivate={(operatorName) => {
                    setDefaultOperator(operatorName)
                  }}
                />
              </div>

              <div
                className={classNames(
                  myStyles.mainNumberContainers,
                  myStyles.container,
                  myStyles.groupingContainer,
                )}
              >
                <FormulaBuilderNumbers />
              </div>
              {isMultiAnswerType && (
                <div
                  className={classNames(
                    myStyles.container,
                    myStyles.groupingContainer,
                    myStyles.mainMatrixQuestionContainer,
                  )}
                >
                  <FormulaBuilderMultiAnswer
                    options={parentField?.value?.options}
                  />
                </div>
              )}
              <div
                className={classNames(
                  myStyles.container,
                  myStyles.groupingContainer,
                )}
              >
                <FormulaBuilderQuestions questionOptions={questionOptions} />
              </div>
            </div>
            <div className={myStyles.mainLayoutLegend}>
              <FormulaBuilderLegend />
            </div>
            <div className={myStyles.mainLayoutActions}>
              <Button
                className={`${managementStyles.button}`}
                onClick={() => {
                  saveChanges()
                  onClose()
                }}
                label="Save"
              />
            </div>
          </div>
        </DndContext>
      </AssessmentFormulaBuilderContext.Provider>
    </Dialog>
  )
}
